import { JsonLd } from "../components/JsonLd";
import { Seo } from "../components/Seo";
import { useState, useEffect, useMemo } from "react";
import { Link, useParams, useSearchParams, Navigate } from "react-router-dom";
import { ChevronRight, Download, FileText, Info, Headphones, Check, Sparkles, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import { PageHero } from "../components/PageHero";
import { ContactForm } from "../components/ContactForm";
import { getProduct, getCategory, allProducts } from "../data/products";
import { getEnvironmentBySlug } from "../data/solutions";
import { VG_ORANGE } from "../lib/brand";

export default function ProductDetail() {
  const { categorySlug, productSlug } = useParams();
  const [searchParams] = useSearchParams();
  const product = getProduct(categorySlug, productSlug);
  const category = getCategory(categorySlug);

  const fromSolutions = searchParams.get("from") === "solutions";
  const recommendedEnv = fromSolutions ? getEnvironmentBySlug(searchParams.get("env")) : null;

  // Certification badges: product-specific ones, plus the CRI Green Label Plus on
  // all Broadloom & Carpet Tile products (except Senator SoftStep Plus).
  const certList = useMemo(() => {
    const base = product?.certifications ? [...product.certifications] : [];
    const carpetCats = ["broadloom-sheet-roll-goods", "carpet-tile-goods"];
    if (carpetCats.includes(categorySlug) && productSlug !== "senator-softstep-plus") {
      base.push({
        src: "/certifications/cri-green-label-plus.png",
        alt: "CRI Green Label Plus - Indoor Air Quality Carpet Testing Program",
      });
    }
    return base;
  }, [product, categorySlug, productSlug]);

  const gallery = useMemo(() => {
    if (!product) return [];
    if (product.gallery) return product.gallery;
    return Array.from({ length: 5 }, (_, i) => ({ src: product.image, label: `Image ${i + 1} of 5` }));
  }, [product]);

  const swatches = useMemo(() => {
    if (!product || product.hideSwatches) return [];
    return product.swatches && product.swatches.length
      ? product.swatches
      : Array.from({ length: product.swatchCount || 38 }, () => ({ name: "Swatch name", src: product.image }));
  }, [product]);

  // Combined carousel slides: gallery images first, then swatch views.
  const slides = useMemo(
    () => [
      ...gallery.map((g) => ({ src: g.src, label: g.label, fit: g.fit, box: g.box })),
      ...swatches.map((s) => ({ src: s.src, label: s.name })),
    ],
    [gallery, swatches]
  );

  const [api, setApi] = useState(null);

  // Related products: prefer same category (real imagery), then fill from others.
  const related = useMemo(() => {
    if (!product) return [];
    const isReal = (p) => !p.isPlaceholder;
    const notThis = (p) => !(p.categorySlug === categorySlug && p.slug === productSlug);
    const sameCat = allProducts.filter((p) => p.categorySlug === categorySlug && notThis(p) && isReal(p));
    const others = allProducts.filter((p) => p.categorySlug !== categorySlug && isReal(p));
    const pick = [...sameCat];
    for (const p of others) {
      if (pick.length >= 4) break;
      pick.push(p);
    }
    return pick.slice(0, 4);
  }, [product, categorySlug, productSlug]);
  const [current, setCurrent] = useState(0);
  const [sampleOpen, setSampleOpen] = useState(false);
  const [sampleColor, setSampleColor] = useState("");
  useEffect(() => {
    if (!api) return;
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    onSelect();
    api.on("select", onSelect);
    return () => api.off("select", onSelect);
  }, [api]);

  if (!product) return <Navigate to="/products" replace />;

  const sampleColorNames =
    product.swatches && product.swatches.length ? product.swatches.map((s) => s.name) : [];

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const productDesc =
    (Array.isArray(product.description) ? product.description[0] : product.description) ||
    `${product.name} — ${product.categoryName} from van Gelder, Inc. commercial flooring.`;
  const productLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: productDesc,
    image: product.image ? `${origin}${product.image}` : `${origin}/og-image.jpg`,
    category: product.categoryName,
    brand: { "@type": "Brand", name: "van Gelder, Inc." },
    url: `${origin}/products/${category.slug}/${product.slug}`,
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
      { "@type": "ListItem", position: 2, name: "Products", item: `${origin}/products` },
      { "@type": "ListItem", position: 3, name: category.name, item: `${origin}/products/${category.slug}` },
      { "@type": "ListItem", position: 4, name: product.name, item: `${origin}/products/${category.slug}/${product.slug}` },
    ],
  };

  return (
    <div data-testid="product-detail-page">
      <JsonLd data={productLd} />
      <JsonLd data={breadcrumbLd} />
      <Seo
        title={product.name}
        description={
          (Array.isArray(product.description) ? product.description[0] : product.description) ||
          `${product.name} — ${product.categoryName} from van Gelder, Inc. commercial flooring.`
        }
        image={product.image}
        type="product"
      />
      <PageHero title={product.name} subtitle={product.categoryName} testId="product-hero" />

      <div className="mx-auto max-w-7xl px-4 py-6">
        <nav className="flex items-center gap-1.5 text-sm text-neutral-500 mb-6 flex-wrap" data-testid="breadcrumb">
          <Link to="/" className="hover:text-[#ED8B00]">Home</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to="/products" className="hover:text-[#ED8B00]">Products</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to={`/products/${category.slug}`} className="hover:text-[#ED8B00]">{category.name}</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-neutral-900 font-medium">{product.name}</span>
        </nav>

        <div className="grid gap-10 lg:grid-cols-2 pb-14">
          {/* Image carousel */}
          <div className="min-w-0">
            <Carousel className="w-full" opts={{ loop: true }} setApi={setApi} data-testid="product-carousel">
              <CarouselContent>
                {slides.map((g, i) => (
                  <CarouselItem key={i}>
                    <div className={`relative ${g.box === "wide" ? "aspect-[16/6]" : "aspect-[4/3]"} overflow-hidden rounded-xl border border-neutral-200 bg-neutral-100`}>
                      <img
                        src={g.src}
                        alt={`${product.name} ${g.label || ""}`}
                        className={`h-full w-full ${g.fit === "contain" ? "object-contain bg-white p-4" : "object-cover"}`}
                      />
                      <span className="absolute bottom-3 left-3 rounded-md bg-white/90 px-3 py-1 text-xs font-medium text-neutral-600">
                        {g.label || "Product image placeholder — final image coming soon"}
                      </span>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="left-3" data-testid="carousel-prev" />
              <CarouselNext className="right-3" data-testid="carousel-next" />
            </Carousel>

            {/* Spec sheet downloads */}
            {(product.productPdf || product.techSpecs) ? (
              <div className="mt-4 grid grid-cols-2 gap-3">
                {product.productPdf ? (
                  <a
                    href={product.productPdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="product-pdf-btn"
                    className="flex items-center gap-2 rounded-lg border border-neutral-200 px-4 py-3 text-sm font-medium text-neutral-700 hover:border-[#ED8B00] hover:text-[#ED8B00] transition-colors"
                  >
                    <FileText className="h-4 w-4" /> Product PDF
                  </a>
                ) : (
                  <button disabled data-testid="product-pdf-btn" className="flex items-center gap-2 rounded-lg border border-neutral-200 px-4 py-3 text-sm font-medium text-neutral-300 cursor-not-allowed">
                    <FileText className="h-4 w-4" /> Product PDF
                  </button>
                )}
                {product.techSpecs ? (
                  <a
                    href={product.techSpecs}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="product-specs-btn"
                    className="flex items-center gap-2 rounded-lg border border-neutral-200 px-4 py-3 text-sm font-medium text-neutral-700 hover:border-[#ED8B00] hover:text-[#ED8B00] transition-colors"
                  >
                    <Download className="h-4 w-4" /> Technical Specs
                  </a>
                ) : (
                  <button disabled data-testid="product-specs-btn" className="flex items-center gap-2 rounded-lg border border-neutral-200 px-4 py-3 text-sm font-medium text-neutral-300 cursor-not-allowed">
                    <Download className="h-4 w-4" /> Technical Specs
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <button
                    data-testid="product-pdf-btn"
                    className="flex items-center gap-2 rounded-lg border border-neutral-200 px-4 py-3 text-sm font-medium text-neutral-700 hover:border-[#ED8B00] hover:text-[#ED8B00] transition-colors"
                    title="Spec sheet placeholder"
                  >
                    <FileText className="h-4 w-4" /> Product PDF
                  </button>
                  <button
                    data-testid="product-specs-btn"
                    className="flex items-center gap-2 rounded-lg border border-neutral-200 px-4 py-3 text-sm font-medium text-neutral-700 hover:border-[#ED8B00] hover:text-[#ED8B00] transition-colors"
                    title="Spec sheet placeholder"
                  >
                    <Download className="h-4 w-4" /> Technical Specs
                  </button>
                </div>
                <p className="mt-2 text-xs text-neutral-400">
                  Download links are placeholders. Spec sheets will be uploaded later.
                </p>
              </>
            )}

            {/* Color swatch gallery */}
            {!product.hideSwatches && (
              <div className="mt-6">
                <h3 className="font-heading font-bold text-neutral-900 mb-3">
                  Available Colors <span className="text-sm font-normal text-neutral-400">({swatches.length})</span>
                </h3>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="swatch-gallery">
                  {swatches.map((s, i) => {
                    const slideIndex = gallery.length + i;
                    const isActive = current === slideIndex;
                    return (
                      <div
                        key={i}
                        className={`group relative aspect-square overflow-hidden rounded-md border transition-all hover:z-10 hover:shadow-md ${
                          isActive ? "border-[#ED8B00] ring-2 ring-[#ED8B00]" : "border-neutral-200"
                        }`}
                      >
                        <button
                          type="button"
                          data-testid={`swatch-${i}`}
                          onClick={() => api && api.scrollTo(slideIndex)}
                          className="absolute inset-0 h-full w-full cursor-pointer"
                          title={`Preview ${s.name}`}
                        >
                          <img src={s.src} alt={s.name} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                          <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/60 via-black/10 to-transparent p-1.5">
                            <span className="text-[10px] font-medium leading-tight text-white drop-shadow-sm">{s.name}</span>
                          </div>
                        </button>
                        <button
                          type="button"
                          data-testid={`swatch-sample-${i}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSampleColor(s.name);
                            setSampleOpen(true);
                          }}
                          title={`Order a free sample of ${s.name}`}
                          aria-label={`Order a free sample swatch of ${product.name} in ${s.name}`}
                          className="absolute top-1 right-1 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-neutral-700 shadow-sm transition-all hover:bg-[#ED8B00] hover:text-white sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100"
                        >
                          <Package className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
                {product.swatchNote && (
                  <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900" data-testid="swatch-note">
                    {product.swatchNote}
                  </p>
                )}
                <p className="mt-2 flex items-center gap-1.5 text-xs text-neutral-500">
                  <Package className="h-3.5 w-3.5 text-[#ED8B00]" />
                  Tap the <span className="font-medium text-neutral-700">sample icon</span> on any color to order a free swatch.
                </p>
              </div>
            )}

            {/* Controlled per-color free sample dialog */}
            <Dialog open={sampleOpen} onOpenChange={setSampleOpen}>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Order Free Samples — {product.name}</DialogTitle>
                  <DialogDescription>
                    Pick the colors and size you'd like, tell us where to ship, and we'll send your free samples.
                  </DialogDescription>
                </DialogHeader>
                <ContactForm
                  key={sampleColor}
                  department="Request Samples"
                  defaultReason="Sample Request"
                  formType="request_samples"
                  productName={product.name}
                  sampleColors={sampleColorNames}
                  initialSelectedColors={sampleColor ? [sampleColor] : []}
                  showShipping
                  compact
                  testId="swatch-sample-form"
                />
              </DialogContent>
            </Dialog>
          </div>

          {/* Details */}
          <div>
            {recommendedEnv && (
              <Link
                to={`/solutions?env=${recommendedEnv.slug}`}
                data-testid="recommended-badge"
                className="mb-3 flex w-fit items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: VG_ORANGE }}
              >
                <Sparkles className="h-3.5 w-3.5" />
                Recommended for {recommendedEnv.name}
              </Link>
            )}
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: VG_ORANGE }}>
              {product.categoryName}
            </span>
            <h2 className="mt-1 font-heading text-2xl sm:text-3xl font-extrabold text-neutral-900 break-words">{product.name}</h2>
            <div className="mt-4 space-y-4 text-neutral-600 leading-relaxed">
              {(Array.isArray(product.description) ? product.description : [product.description]).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>

            {certList && certList.length > 0 && (
              <div className="mt-5 rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3" data-testid="product-certifications">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
                  Certified for low emissions &amp; indoor air quality
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-3">
                  {certList.map((c) => (
                    <img
                      key={c.src}
                      src={c.src}
                      alt={c.alt}
                      className={`w-auto object-contain ${c.src.includes("cri-green-label-plus") ? "h-[55px]" : "h-10"}`}
                      loading="lazy"
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6">
              <h3 className="font-heading font-bold text-neutral-900">Best-Use Applications</h3>
              <ul className="mt-3 space-y-2">
                {product.applications.map((a) => (
                  <li key={a} className="flex items-start gap-2 text-sm text-neutral-700">
                    <Check className="h-4 w-4 mt-0.5" style={{ color: VG_ORANGE }} /> {a}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6">
              <h3 className="font-heading font-bold text-neutral-900">Features & Specifications</h3>
              <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                {product.features.map((f, i) =>
                  typeof f === "object" ? (
                    <li key={i} className="rounded-md bg-neutral-50 border border-neutral-200 px-3 py-2 text-sm">
                      <span className="block text-xs font-semibold uppercase tracking-wide text-neutral-400">{f.label}</span>
                      <span className="text-neutral-700">{f.value}</span>
                    </li>
                  ) : (
                    <li key={i} className="rounded-md bg-neutral-50 border border-neutral-200 px-3 py-2 text-sm text-neutral-700">
                      {f}
                    </li>
                  )
                )}
              </ul>
            </div>

            {/* CTAs */}
            <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    className="text-white hover:opacity-90 h-12 px-6"
                    style={{ backgroundColor: VG_ORANGE }}
                    data-testid="request-info-btn"
                  >
                    <Info className="mr-2 h-5 w-5" /> Request Information
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Request Information — {product.name}</DialogTitle>
                    <DialogDescription>
                      Fill out the form and our team will send you details about {product.name}.
                    </DialogDescription>
                  </DialogHeader>
                  <ContactForm
                    department="Product Information"
                    defaultReason="Product Information"
                    formType="request_info"
                    productName={product.name}
                    compact
                    testId="product-request-form"
                  />
                </DialogContent>
              </Dialog>

              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="h-12 px-6 border-[#ED8B00] text-[#ED8B00] hover:bg-orange-50"
                    data-testid="request-samples-btn"
                  >
                    <Package className="mr-2 h-5 w-5" /> Request Samples
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Request Samples — {product.name}</DialogTitle>
                    <DialogDescription>
                      Pick the colors and size you'd like, tell us where to ship, and we'll send samples your way.
                    </DialogDescription>
                  </DialogHeader>
                  <ContactForm
                    department="Request Samples"
                    defaultReason="Sample Request"
                    formType="request_samples"
                    productName={product.name}
                    sampleColors={sampleColorNames}
                    showShipping
                    compact
                    testId="product-samples-form"
                  />
                </DialogContent>
              </Dialog>

              <Button asChild variant="outline" className="h-12 px-6" data-testid="contact-cs-btn">
                <Link to="/contact">
                  <Headphones className="mr-2 h-5 w-5" /> Contact Customer Service
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Related products */}
        {related.length > 0 && (
          <section className="border-t border-neutral-200 pb-16 pt-12" data-testid="related-products">
            <div className="mb-6 flex items-end justify-between gap-4">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: VG_ORANGE }}>
                  Keep exploring
                </span>
                <h2 className="mt-1 font-heading text-2xl sm:text-3xl font-extrabold text-neutral-900">You may also like</h2>
              </div>
              <Link
                to={`/products/${category.slug}`}
                className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-600 hover:text-[#ED8B00] transition-colors"
                data-testid="related-view-all"
              >
                View all {category.name} <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {related.map((p) => (
                <Link
                  key={`${p.categorySlug}/${p.slug}`}
                  to={`/products/${p.categorySlug}/${p.slug}`}
                  data-testid={`related-card-${p.slug}`}
                  className="group overflow-hidden rounded-xl border border-neutral-200 bg-white transition-all hover:-translate-y-1 hover:border-[#ED8B00] hover:shadow-lg"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-neutral-100">
                    <img
                      src={p.image}
                      alt={p.name}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">{p.categoryName}</span>
                    <h3 className="mt-0.5 font-heading font-bold text-neutral-900 leading-snug line-clamp-2 group-hover:text-[#ED8B00] transition-colors">
                      {p.name}
                    </h3>
                    <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-[#ED8B00]">
                      View product <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
